import type { Params } from "../toQueryString";
import { absolutePath } from "./absolutePath";

interface SettingsFnProps {
  id?: string | number;
  options?: Record<string, unknown>;
  params?: Record<string, unknown> | object;
  uuid?: string | number;
}

interface CollectionParams {
  params?: Params;
}

interface ItemParams {
  pageId: string | number;
  params?: Params;
}

interface RevisionParams {
  pageId: string | number;
  params?: Params;
  revisionId: string | number;
}

export const settingsSites = {
  settings: {
    show: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/site/settings", params),
    update: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/site/settings", params),
  },
  pages: {
    index: ({ params = {} }: CollectionParams = {}) =>
      absolutePath("settings/site/pages", params),
    create: ({ params = {} }: CollectionParams = {}) =>
      absolutePath("settings/site/pages", params),
    destroy: ({ pageId }: ItemParams) =>
      absolutePath(`settings/site/pages/${pageId}`),
    show: ({ params = {}, pageId }: ItemParams) =>
      absolutePath(`settings/site/pages/${pageId}`, params),
    update: ({ params = {}, pageId }: ItemParams) =>
      absolutePath(`settings/site/pages/${pageId}`, params),
    updateSettings: ({ params = {}, pageId }: ItemParams) =>
      absolutePath(`settings/site/pages/${pageId}/settings`, params),
    publish: ({ pageId }: ItemParams) =>
      absolutePath(`settings/site/pages/${pageId}/publish`),
    unpublish: ({ pageId }: ItemParams) =>
      absolutePath(`settings/site/pages/${pageId}/unpublish`),
    duplicate: ({ pageId }: ItemParams) =>
      absolutePath(`settings/site/pages/${pageId}/duplicate`),
    revisions: {
      index: ({ params = {}, pageId }: Omit<RevisionParams, "revisionId">) =>
        absolutePath(`settings/site/pages/${pageId}/revisions`, params),
      show: ({ params = {}, pageId, revisionId }: RevisionParams) =>
        absolutePath(
          `settings/site/pages/${pageId}/revisions/${revisionId}`,
          params,
        ),
      restore: ({ params = {}, pageId, revisionId }: RevisionParams) =>
        absolutePath(
          `settings/site/pages/${pageId}/revisions/${revisionId}/restore`,
          params,
        ),
    },
    themes: {
      update: ({ pageId }: { pageId: number }) =>
        absolutePath(`settings/site/pages/${pageId}/change_theme`),
    },
    custom_fonts: {
      index: ({ pageId }: { pageId: number }) =>
        absolutePath(`settings/site/pages/${pageId}/custom_fonts`),
      show: ({ pageId, id }: { pageId: number; id: number }) =>
        absolutePath(`settings/site/pages/${pageId}/custom_fonts/${id}`),
      create: ({ pageId }: { pageId: number }) =>
        absolutePath(`settings/site/pages/${pageId}/custom_fonts`),
      destroy: ({ pageId, id }: { pageId: number; id: number }) =>
        absolutePath(`settings/site/pages/${pageId}/custom_fonts/${id}`),
    },
  },
  themes: {
    index: () => absolutePath(`settings/site/themes`),
    update: ({ themeId }: { themeId: number }) =>
      absolutePath(`settings/site/themes/${themeId}`),
    create_custom_theme: () =>
      absolutePath("settings/site/themes/create_custom_theme"),
    delete: ({ themeId }: { themeId: number }) =>
      absolutePath(`settings/site/themes/${themeId}`),
    show: ({ themeId }: { themeId: number }) =>
      absolutePath(`settings/site/themes/${themeId}`),
  },
  dynamicContent: {
    newsletters: {
      index: ({ params = {} }: CollectionParams = {}) =>
        absolutePath("settings/site/dynamic_content/newsletters", params),
    },
    newsletterPosts: {
      index: ({ params = {} }: CollectionParams = {}) =>
        absolutePath("settings/site/dynamic_content/newsletter_posts", params),
    },
  },
  redirects: {
    index: ({ params = {} }: CollectionParams = {}) =>
      absolutePath("settings/site/redirects", params),
    create: ({ params = {} }: CollectionParams = {}) =>
      absolutePath("settings/site/redirects", params),
    destroy: ({ redirectId }: { redirectId: string | number }) =>
      absolutePath(`settings/site/redirects/${redirectId}`),
    update: ({
      params = {},
      redirectId,
    }: {
      params?: Params;
      redirectId: string | number;
    }) => absolutePath(`settings/site/redirects/${redirectId}`, params),
  },
  reusableBlocks: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/reusable_blocks", params),
    create: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/reusable_blocks", params),
    show: ({ id, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/reusable_blocks/${id}`, params),
    update: ({ id, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/reusable_blocks/${id}`, params),
    destroy: ({ id }: SettingsFnProps = {}) =>
      absolutePath(`settings/reusable_blocks/${id}`),
  },
};
